import { type FC, useRef } from "react";

import { cn } from "@/lib/utils";
import { DictationButton } from "@recobra/ui/custom/dictation-button";
import { Field, FieldError, FieldLabel } from "@recobra/ui/field";
import { InputGroup, InputGroupAddon, InputGroupTextarea } from "@recobra/ui/input-group";
import { Textarea } from "@recobra/ui/textarea";

import { getErrorMessage, useFieldContext } from "./context";

export const TextAreaField: FC<{
  label: string;
  id: string;
  rows?: number;
  placeholder?: string;
  colSpan?: 1 | 2;
  required?: boolean;
  dictation?: boolean;
  maxLength?: number;
  className?: string;
  textareaClassName?: string;
}> = ({
  label,
  id,
  rows = 3,
  placeholder,
  colSpan,
  required,
  dictation = false,
  maxLength,
  className,
  textareaClassName,
}) => {
  const field = useFieldContext<string | undefined>();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const errorMessage = getErrorMessage(field.state.meta.errors[0]);
  const value = field.state.value ?? "";
  const TextareaControl = dictation ? InputGroupTextarea : Textarea;
  const textarea = (
    <TextareaControl
      ref={textareaRef}
      id={id}
      rows={rows}
      maxLength={maxLength}
      aria-required={required}
      placeholder={placeholder}
      value={value}
      onChange={(e) => field.handleChange(e.target.value || undefined)}
      onBlur={field.handleBlur}
      aria-invalid={Boolean(errorMessage)}
      className={textareaClassName}
    />
  );

  return (
    <Field
      data-invalid={errorMessage ? "true" : undefined}
      className={cn(colSpan === 2 ? "col-span-2" : "min-w-0", className)}
    >
      <FieldLabel htmlFor={id}>
        {label}
        {required && <span className="text-destructive">*</span>}
      </FieldLabel>
      {dictation ? (
        <InputGroup>
          {textarea}
          <InputGroupAddon align="block-end">
            <DictationButton
              targetRef={textareaRef}
              value={value}
              onChange={(next) => field.handleChange(next || undefined)}
            />
          </InputGroupAddon>
        </InputGroup>
      ) : (
        textarea
      )}
      {errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
    </Field>
  );
};
